import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { MEDAL_COLORS } from './WorldCupLeaderboard.constants';

export interface LeaderboardCardStyleOptions {
  compactRank?: boolean;
}

export const getLeaderboardCardStyles = (
  theme: Theme,
  _options?: LeaderboardCardStyleOptions,
) => {
  return StyleSheet.create({
    pressRoot: {
      marginHorizontal: 12,
      marginBottom: 8,
      borderRadius: 14,
      overflow: 'hidden',
    },
    cardOuter: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.gray[200] + '33',
      backgroundColor: theme.background.card + 'D9',
      paddingVertical: 8,
      paddingHorizontal: 12,
      position: 'relative',
      ...(Platform.OS === 'web' && {
        backdropFilter: 'saturate(140%) blur(10px)' as never,
        WebkitBackdropFilter: 'saturate(140%) blur(10px)' as never,
        boxShadow: `0 2px 16px ${theme.black}26, inset 0 1px 0 ${theme.white}12` as never,
      }),
    },
    cardOuterRank1: {
      borderColor: MEDAL_COLORS.gold + '66',
    },
    cardOuterRank2: {
      borderColor: MEDAL_COLORS.silver + '55',
    },
    cardOuterRank3: {
      borderColor: MEDAL_COLORS.bronze + '55',
    },
    cardOuterMe: {
      borderColor: theme.primary + '80',
      borderLeftWidth: 3,
      borderLeftColor: theme.primary,
      backgroundColor: theme.primary + '14',
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 14px 2px ${theme.primary}33` as never,
      }),
    },
    menuTriggerWrap: {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 2,
    },
    menuTriggerButton: {
      padding: 4,
      borderRadius: 8,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    colAlignCenter: {
      justifyContent: 'center',
    },
    rankCol: {
      width: 22,
      alignItems: 'center',
    },
    rankNumberText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    avatarCol: {
      width: 36,
      height: 36,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    nameCol: {
      flex: 1,
      minWidth: 0,
      flexShrink: 1,
    },
    nameRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 6,
    },
    // Under the name, in the name column's existing slack — the card does not grow
    // (specs/rank-on-social-screens §7).
    rankLevelPill: { marginTop: 3 },
    userName: {
      flexShrink: 1,
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[800],
    },
    userNameMe: {
      color: theme.primary,
      fontWeight: '800',
    },
    hitsBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 999,
      backgroundColor: theme.accent,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 2px 4px ${theme.accent}55` as never,
      }),
    },
    hitsBadgeText: {
      fontSize: 9,
      fontWeight: '900',
      color: theme.background.primary,
      letterSpacing: 0.3,
    },
    /** Two tiles side-by-side: each is a small square + label beneath. */
    pickPair: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 6,
      flexShrink: 0,
    },
    pickTileCol: {
      alignItems: 'center',
      gap: 3,
      minWidth: 36,
      maxWidth: 50,
    },
    flagTile: {
      width: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: theme.background.inset,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' as never,
      }),
    },
    kitTile: {
      width: 32,
      height: 32,
      borderRadius: 6,
      backgroundColor: theme.background.inset,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      ...(Platform.OS === 'web' && {
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' as never,
      }),
    },
    pickCaption: {
      maxWidth: 50,
      fontSize: 9,
      fontWeight: '700',
      color: theme.gray[700],
      letterSpacing: 0.1,
      textAlign: 'center',
      lineHeight: 11,
      ...(Platform.OS === 'android' && {
        includeFontPadding: false,
      }),
    },
    ptsWrap: {
      minWidth: 42,
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    ptsCol: {
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: 3,
    },
    livePointsDelta: {
      fontSize: 9,
      fontWeight: '900',
      color: theme.success,
      letterSpacing: 0.2,
      marginTop: 1,
      fontVariant: ['tabular-nums'],
    },
    pointsValue: {
      fontSize: 16,
      fontWeight: '900',
      color: theme.gray[900],
      letterSpacing: -0.3,
      textAlign: 'right',
      fontVariant: ['tabular-nums'],
    },
    pointsLabel: {
      fontSize: 9,
      fontWeight: '800',
      color: theme.gray[500],
      letterSpacing: 0.4,
    },
    pressedOpacity: {
      opacity: 0.92,
    },
    hoverWeb: {
      ...(Platform.OS === 'web' && {
        borderColor: theme.primary + '66',
      }),
    },
    liveStrip: {
      marginTop: 8,
      borderTopWidth: 1,
      borderTopColor: theme.gray[200] + '66',
      paddingTop: 6,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'nowrap',
    },
    /**
     * Holds every live-game chip on a single row. We deliberately do NOT wrap —
     * with 2 games on a small device the row stays aligned by letting each item
     * flex-shrink to share the remaining width with the LIVE badge.
     */
    liveGamesInline: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      columnGap: 6,
      flex: 1,
      minWidth: 0,
    },
    /**
     * One slot in the single-row live strip — `flex: 1` so multiple items split the
     * remaining width evenly; `minWidth: 0` and inner `flexShrink: 1` on the chip let
     * the slot compress on narrow screens without bumping anything to a second line.
     */
    liveGameItem: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flexShrink: 1,
      minWidth: 0,
    },
    /**
     * Single bordered chip wrapping [home flag][score][away flag][×2?] — same
     * visual language as the podium chips so live indicators read consistently.
     * Outcome is conveyed by the chip's border + tinted background; no separate
     * HIT/RESULT/MISS pill.
     */
    liveGameChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 5,
      paddingVertical: 2,
      borderRadius: 6,
      borderWidth: 1,
      flexShrink: 1,
      minWidth: 0,
    },
    liveGameChipHit: {
      backgroundColor: theme.success + '22',
      borderColor: theme.success + '99',
    },
    liveGameChipResult: {
      backgroundColor: theme.info + '22',
      borderColor: theme.info + '99',
    },
    liveGameChipMiss: {
      backgroundColor: theme.error + '1f',
      borderColor: theme.error + '88',
    },
    liveScoreText: {
      fontSize: 11,
      color: theme.gray[800],
      fontWeight: '900',
      minWidth: 22,
      textAlign: 'center',
      letterSpacing: 0.2,
    },
    liveScoreTextHit: { color: theme.success },
    liveScoreTextResult: { color: theme.info },
    liveScoreTextMiss: { color: theme.error },
    liveBadge: {
      backgroundColor: theme.error,
      borderRadius: 999,
      paddingHorizontal: 8,
      paddingVertical: 2,
    },
    liveBadgeText: {
      color: theme.textOnStatusBadge,
      fontSize: 10,
      fontWeight: '800',
      letterSpacing: 0.3,
    },
    liveStripLabel: {
      flexShrink: 1,
      fontSize: 11,
      color: theme.gray[700],
      fontWeight: '700',
    },
    liveStripPrediction: {
      fontSize: 11,
      color: theme.gray[900],
      fontWeight: '900',
      width: 34,
      textAlign: 'center',
      minWidth: 0,
    },
    /** Overflow marker on a capped live strip (AC-18.1). Sits inline with the last chip. */
    liveMoreGames: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[400],
      alignSelf: 'center',
      paddingHorizontal: 4,
    },
    livePointsText: {
      fontSize: 11,
      color: theme.gray[700],
      fontWeight: '800',
      textAlign: 'left',
    },
    // Subtle "90'" marker shown on a live chip whose outcome was judged on the 90-minute
    // result while the running scoreline diverges (knockout with extra-time goals). US-9.
    live90MinCue: {
      fontSize: 9,
      color: theme.gray[500],
      fontWeight: '700',
    },
    /**
     * Fixed-width slot at the end of every live-score chip. Always renders a
     * Zap icon — gold when the game's pick is on a Golden Ball boost, otherwise
     * faded grey. Reserving the slot in every chip keeps chip widths uniform
     * so flag/score positions stay aligned across rows.
     */
    liveBoostSlot: {
      width: 13,
      marginLeft: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    /** Gold drop-shadow halo applied to the slot when the boost is active. */
    liveBoostGlyph: {
      ...(Platform.OS === 'web' && {
        filter: `drop-shadow(0 0 4px ${theme.accent}aa)` as never,
      }),
    },
    liveStripNoPick: {
      fontSize: 11,
      color: theme.gray[500],
      fontWeight: '700',
    },
  });
};
